import { ActionType } from 'typesafe-actions';

import { CommonContextAction } from 'common/store/context/actionTypes';
import { HitData } from 'common/store/common/search/types';
import * as actions from './actions';
import { GradingPartyDocument } from '../../services/gradingPartyService';
import { EvaluatorScoreEntry } from '../../features/table/types';

export type ContextExtraParameters = keyof PartyContextValues | keyof QuestionContextValues;

export enum ContextTypeKeys {
    ASSESSMENT_RUN_ID_UPDATED = 'context/ASSESSMENT_RUN_ID_UPDATED',
    PARTY_ID_UPDATED = 'context/PARTY_ID_UPDATED',
    COMMITTEE_ID_SELECTED = 'context/COMMITTEE_ID_SELECTED',
    QUESTION_ID_UPDATED = 'context/QUESTION_ID_UPDATED',
    WORKSPACE_ID_UPDATED = 'context/WORKSPACE_ID_UPDATED',
    SET_PARTY_STATE = 'context/SET_PARTY_STATE',
    SET_QUESTION_STATE = 'context/SET_QUESTION_STATE',
    SETUP_WORKSPACES = 'context/SETUP_WORKSPACES',
    LOAD_MISSING_PROPERTIES = 'context/LOAD_MISSING_PROPERTIES',
    REFRESH_MISSING_PROPERTIES = 'context/REFRESH_MISSING_PROPERTIES',
    CONTEXT_PROPERTIES_UPDATED = 'context/CONTEXT_PROPERTIES_UPDATED',
    GET_GRADING_WORKFLOW_INFO = 'context/GET_GRADING_WORKFLOW_INFO',
    SET_GRADING_WORKFLOW_INFO = 'context/SET_GRADING_WORKFLOW_INFO',
    TOGGLE_DETAILS_PANEL = 'context/TOGGLE_DETAILS_PANEL',
    OPEN_RECORDING_PANEL = 'context/OPEN_RECORDING_PANEL',
    OPEN_RECORDING_PANEL_DONE = 'context/OPEN_RECORDING_PANEL_DONE',
    UPDATE_CONTEXT_PARTY_QUESTION_REVISION_IDS = 'context/UPDATE_CONTEXT_PARTY_QUESTION_REVISION_IDS',
    LOAD_ASSESSMENT_RUN_SESSION = 'context/LOAD_ASSESSMENT_RUN_SESSION',
    ASSESSMENT_RUN_SESSION_LOADED = 'context/ASSESSMENT_RUN_SESSION_LOADED',
    TOGGLE_SPEAKING_TEST_ID_DATA_OPEN_SCREEN = 'context/TOGGLE_SPEAKING_TEST_ID_DATA_OPEN_SCREEN',
    SPEAKING_QUESTION_SET_IDS_LOADED = 'context/SPEAKING_QUESTION_SET_IDS_LOADED',
    SET_SPEAKING_SELECTED_CONTENT_ITEM_ID = 'context/SET_SPEAKING_SELECTED_TASK_SECTION_ID',
    SPEAKING_REFRESH_HEARTBEAT = 'context/SPEAKING_REFRESH_HEARTBEAT',
    SUBMISSION_DELETED = 'context/SUBMISSION_DELETED',
    UPDATE_QUESTION_STATE = 'context/UPDATE_QUESTION_STATE',
    TOGGLE_WORKSPACE_DETAILED_VIEW = 'context/TOGGLE_WORKSPACE_DETAILED_VIEW',
    UPDATE_WORKFLOW_INFO = 'context/UPDATE_WORKFLOW_INFO',
}

export type ContextAction = ActionType<typeof actions> | CommonContextAction;

export enum GradingStatus {
    CREATED = 'created',
    SCHEDULED = 'scheduled',
    ACTIVE = 'active',
    FINISHED = 'finished',
    GRADING_IN_PROGRESS = 'grading_in_progress',
    GRADED = 'graded',
    REASSESSMENT_IN_PROGRESS = 'reassessment_in_progress',
}

export type ContextUrlSearchStringObject = {
    assessmentRunId?: number;
    workspaceId?: string;
    partyId?: number;
    questionId?: number;
    selectedCommitteeId?: number;
    showDetailedView?: boolean;
};

export type PartyContextValues = {
    partyName?: string;
    partyCommitteeId?: number; // committeeId of the current party
    partyId?: number;
    gradingPartyDocument?: GradingPartyDocument;
    questionsList?: HitData[];
};

export type QuestionContextValues = {
    questionNumber?: number;
    questionNumbering?: string;
    questionType?: string;
    documentContentRevisionId?: number;
    questionName?: string;
    answered?: boolean;
    score?: number;
    evaluatorScores?: EvaluatorScoreEntry[];
    maxScore?: number;
    questionWeight?: number;
    rubricType?: string;
    hasStimulusUserResponse?: boolean;
    stimulusContentRevisionId?: number;
    noQuestionFound?: boolean;
    hasMaximaValidationIssue?: boolean;
};

export enum WorkflowState {
    NOT_FINISHED = 'not_finished',
    NOT_READY_FOR_GRADING = 'not_ready_for_grading',
    READY_FOR_GRADING = 'ready_for_grading',
    SINGLE_GRADER = 'single_grader',
    OWN_GRADING_STARTED = 'own_grading_started',
    OWN_GRADING_CONFIRMED_WAITING = 'own_grading_confirmed_waiting',
    FINAL_GRADING_CONFLICTS = 'final_grading_conflicts',
    FINAL_GRADING_ALLOWED = 'final_grading_allowed',
    FINAL_GRADING_CONFIRMED_WAITING = 'final_grading_confirmed_waiting',
    GRADING_COMPLETED = 'grading_completed',
}

export enum WorkflowPrimaryActions {
    END_TEST = 'end_test',
    SHARE_OWN_GRADES = 'share_own_grades',
    ALIGN_GRADES = 'align_grades',
    CONFIRM_FINAL_GRADES = 'confirm_final_grades',
    DOWNLOAD_PROTOCOL = 'download_protocol',
}

export enum ModerationStatus {
    NOT_REVIEWED = 'NOT_REVIEWED',
    SATISFACTORY = 'SATISFACTORY',
    NEEDS_REVISING = 'NEEDS_REVISING',
    UNSATISFACTORY = 'UNSATISFACTORY',
    READY_FOR_MODERATION = 'READY_FOR_MODERATION',
}

export enum ModerationPrimaryActions {
    SET_MODERATION_STATUS = 'set_moderation_status',
}
